import { useEffect } from 'react'
import { useResizeDetector } from 'react-resize-detector'

import { GraphType, GraphDefaultProps, GraphRender } from '../common'
import { useSingleBarGraphOption, SingleBarGraphOptions } from './container/options'
import { useSingleBarGraphModel } from './container/model'
import { useSingleBarGraphRenderOption } from './container/render'
import { useSingleBarGraphEvents } from './container/event'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components'

import { Box } from '@mui/material'

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  BarChart,
  CanvasRenderer,
])

const SingleBarGraph = <T,>({
  dashBoard,
  targetList,
  dataList,
  conditions,
  onChangeCondition,
  itemData,
  isResizing,
  graphRef,
  setCsv
}: GraphDefaultProps<T>) => {
  const { options, filterText } = itemData
  const { width, height, ref } = useResizeDetector()

  const graphOption = useSingleBarGraphOption({ dashBoard, options, targetList })
  const graphData = useSingleBarGraphModel({ dataList, conditions, filterText, graphOption })
  const renderOption = useSingleBarGraphRenderOption({ graphOption, graphData })
  const events = useSingleBarGraphEvents({ graphOption, onChangeCondition })

  useEffect(
    () => setCsv({ builder: () => graphData?.csvBuilder() }),
    [graphData, setCsv]
  )

  const { axisDef, valueDef } = graphOption
  return (
    <Box ref={ref} width="100%" height="100%">
      <GraphRender
        dashBoard={dashBoard}
        keys={[axisDef?.key, valueDef?.key]}
        invalid={!graphData || isResizing || ((height ?? 0) <= 10)}
      >
        {(width && height) ? (
          <ReactEChartsCore
            ref={graphRef}
            echarts={echarts}
            notMerge={true}
            onEvents={events}
            option={renderOption}
            opts={{ renderer: 'canvas' }}
            style={{ width, height }}
            theme="insite"
          />
        ) : <></>}
      </GraphRender>
    </Box>
  )
}
SingleBarGraph.displayName = 'echartsCustomGraph'

export const SingleBarGraphFactory: GraphType = {
  title: '単一棒グラフ',
  options: SingleBarGraphOptions,
  element: <T,>(props: GraphDefaultProps<T>) => <SingleBarGraph {...props} />
}
